import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART, TOKENS } from "../../lib/designTokens";
import { fmtDate } from "../../lib/format";
import { ProgressChartContainer } from "./ProgressChartContainer";
import { ProgressChartTooltip } from "./ProgressChartTooltip";
import { ProgressSectionState } from "./ProgressSectionStatus";
import {
  PROGRESS_AXIS_LINE,
  PROGRESS_CHART_GRID_PROPS,
  PROGRESS_CHART_MARGIN,
  PROGRESS_CHART_TICK,
  PROGRESS_Y_AXIS_WIDTH,
  CHART_MIN_HEIGHT,
  TRAINING_LOAD_SERIES,
  buildNumericDomain,
  formatChartDateTick,
  formatTooltipNumber,
  getXAxisInterval,
  lineSeriesDot,
} from "./progressChartUtils";

const ChartLegend = ({ series }) => (
  <div className="flex flex-wrap items-center gap-3 mt-3">
    {series.map((item) => (
      <span key={item.key} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: item.color }}
          aria-hidden="true"
        />
        <span style={{ color: item.color }}>{item.label}</span>
      </span>
    ))}
  </div>
);

export const ProgressTrainingLoadDailyChart = ({ data, chartState }) => {
  const domain = buildNumericDomain(data, ["acute_load", "chronic_load", "form"], { includeZero: true });

  return (
    <div className="app-card-elevated p-4 sm:p-5 min-w-0">
      <div className="card-label mb-3">Fitness / Fatigue / Form</div>
      {data.length === 0 ? (
        <ProgressSectionState
          variant="empty"
          description="No daily load series is available for this range."
          testId="training-load-daily-chart-empty"
          className="min-h-64 flex flex-col justify-center border-0 bg-transparent"
        />
      ) : chartState === "all-null" ? (
        <ProgressSectionState
          variant="unavailable"
          description="Daily load rows exist, but acute/chronic/form values are unavailable."
          testId="training-load-daily-chart-unavailable"
          className="min-h-64 flex flex-col justify-center border-0 bg-transparent"
        />
      ) : (
        <>
          <ProgressChartContainer
            title="Fitness fatigue form chart"
            ariaLabel="Fitness, fatigue, and form trends by day"
            testId="training-load-chart"
            chartState={chartState}
            minHeight={CHART_MIN_HEIGHT.training}
            summary="Daily acute load, chronic load, and form series."
          >
            <LineChart data={data} margin={PROGRESS_CHART_MARGIN}>
              <CartesianGrid {...PROGRESS_CHART_GRID_PROPS} />
              <XAxis
                dataKey="date"
                tick={PROGRESS_CHART_TICK}
                tickLine={false}
                axisLine={PROGRESS_AXIS_LINE}
                interval={getXAxisInterval(data.length)}
                minTickGap={10}
                tickFormatter={(value, index) => formatChartDateTick(value, index, data.length)}
              />
              <YAxis
                tick={PROGRESS_CHART_TICK}
                tickLine={false}
                axisLine={PROGRESS_AXIS_LINE}
                width={PROGRESS_Y_AXIS_WIDTH}
                domain={domain}
              />
              <ReferenceLine y={0} stroke={CHART.tick} strokeDasharray="4 4" label="" />
              <RechartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload;
                  const rows = TRAINING_LOAD_SERIES.map((series) => ({
                    label: series.label,
                    value: formatTooltipNumber(row?.[series.key], { decimals: 2 }),
                  })).filter((entry) => entry.value != null);
                  return (
                    <ProgressChartTooltip
                      active
                      payload={payload}
                      label={row?.date ? fmtDate(row.date) : null}
                      rows={rows}
                    />
                  );
                }}
              />
              {TRAINING_LOAD_SERIES.map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={2.25}
                  dot={lineSeriesDot(data.length, series.color)}
                  activeDot={{ r: 5, fill: series.color, stroke: CHART.dotStroke, strokeWidth: 1.5 }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ProgressChartContainer>
          <ChartLegend series={TRAINING_LOAD_SERIES} />
        </>
      )}
    </div>
  );
};

export const ProgressWeeklyLoadChart = ({ data, chartState }) => {
  const domain = buildNumericDomain(data, ["weekly_load"], { includeZero: true });
  const latestIndex = data.length - 1;
  const hasSpike = data.some((row) => row.load_spike === true);
  const loadColor = TOKENS.analytics.amber;

  return (
    <div className="app-card-elevated p-4 sm:p-5 min-w-0">
      <div className="card-label mb-3">Weekly Load</div>
      {data.length === 0 ? (
        <ProgressSectionState
          variant="empty"
          description="No weekly load totals are available for this range."
          testId="weekly-load-chart-empty"
          className="min-h-64 flex flex-col justify-center border-0 bg-transparent"
        />
      ) : chartState === "all-null" ? (
        <ProgressSectionState
          variant="unavailable"
          description="Weekly load rows exist, but totals are unavailable."
          testId="weekly-load-chart-unavailable"
          className="min-h-64 flex flex-col justify-center border-0 bg-transparent"
        />
      ) : (
        <>
          <ProgressChartContainer
            title="Weekly training load chart"
            ariaLabel="Weekly training load totals by week"
            testId="weekly-load-chart"
            chartState={chartState}
            minHeight={CHART_MIN_HEIGHT.training}
            summary={
              hasSpike
                ? "Weekly load chart with at least one load spike week."
                : "Weekly load chart without load spikes."
            }
          >
            <BarChart data={data} margin={PROGRESS_CHART_MARGIN}>
              <CartesianGrid {...PROGRESS_CHART_GRID_PROPS} />
              <XAxis
                dataKey="week_start"
                tick={PROGRESS_CHART_TICK}
                tickLine={false}
                axisLine={PROGRESS_AXIS_LINE}
                interval={getXAxisInterval(data.length)}
                minTickGap={10}
                tickFormatter={(value, index) => formatChartDateTick(value, index, data.length)}
              />
              <YAxis
                tick={PROGRESS_CHART_TICK}
                tickLine={false}
                axisLine={PROGRESS_AXIS_LINE}
                width={PROGRESS_Y_AXIS_WIDTH}
                domain={domain}
              />
              <RechartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload;
                  const rows = [
                    {
                      label: "Weekly load",
                      value: formatTooltipNumber(row?.weekly_load, { decimals: 2 }),
                    },
                  ];
                  if (row?.week_over_week_change_pct != null) {
                    rows.push({
                      label: "Week-over-week",
                      value: formatTooltipNumber(row.week_over_week_change_pct, {
                        decimals: 2,
                        unit: "%",
                      }),
                    });
                  }
                  if (row?.load_spike === true) {
                    rows.push({ label: "Status", value: "Load spike (≥1.5× previous week)" });
                  }
                  return (
                    <ProgressChartTooltip
                      active
                      payload={payload}
                      label={row?.week_start ? fmtDate(row.week_start) : null}
                      rows={rows}
                    />
                  );
                }}
              />
              <Bar
                dataKey="weekly_load"
                fill={loadColor}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
                shape={(props) => {
                  const { x, y, width, height, index, payload } = props;
                  const isLatest = index === latestIndex;
                  const isSpike = payload?.load_spike === true;
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill={loadColor}
                      stroke={isLatest || isSpike ? CHART.hoverLine : "none"}
                      strokeWidth={isLatest || isSpike ? 1.5 : 0}
                      rx={3}
                      ry={3}
                    />
                  );
                }}
              />
            </BarChart>
          </ProgressChartContainer>
          {hasSpike ? (
            <p className="text-[10px] text-analytics-amber mt-2" data-testid="weekly-load-spike-context">
              Load spike detected for at least one week (≥1.5× previous week).
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};
